/* Section: newsletter_popup
   Opens the email-capture modal after the page loads, then closes on the X,
   on Escape, or on a click on the dim behind the card.

   How often it opens is a section setting, read off the root element:
     data-frequency="always"  — every page load / reload (the default)
     data-frequency="session" — once per browser tab session
     data-frequency="once"    — once per browser, until storage is cleared

   Nothing here hides the card by default: the closed state is CSS
   (.newsletter-popup is transparent + pointer-events:none until .is-open),
   so a visitor whose JS fails to load gets no popup rather than a dead
   overlay pinned over the page.

   In the theme editor the popup ignores frequency entirely and reopens on
   section select, otherwise a merchant could only ever style it once. */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'munchief:newsletter-popup:';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function storageFor(frequency) {
    try {
      return frequency === 'session' ? window.sessionStorage : window.localStorage;
    } catch (error) {
      /* Private mode / blocked storage — treat it as "never seen before". */
      return null;
    }
  }

  function hasSeen(root) {
    var frequency = root.dataset.frequency;
    if (frequency === 'always') return false;

    var store = storageFor(frequency);
    if (!store) return false;

    try {
      return store.getItem(STORAGE_PREFIX + root.dataset.storageKey) === 'seen';
    } catch (error) {
      return false;
    }
  }

  function markSeen(root) {
    var frequency = root.dataset.frequency;
    if (frequency === 'always') return;

    var store = storageFor(frequency);
    if (!store) return;

    try {
      store.setItem(STORAGE_PREFIX + root.dataset.storageKey, 'seen');
    } catch (error) {
      /* Quota or blocked storage — the popup just shows again next visit. */
    }
  }

  function init(root) {
    if (root.dataset.newsletterPopupReady === 'true') return;
    root.dataset.newsletterPopupReady = 'true';

    var dialog = root.querySelector('[data-newsletter-popup-dialog]');
    /* Both dismiss controls carry the hook — the corner X and the "no
       thanks" link under the call to action — so this has to be an ALL
       query. A singular querySelector bound whichever came first in the
       markup and left the other one dead. */
    var closeButtons = root.querySelectorAll('[data-newsletter-popup-close]');
    var closeButton = closeButtons[0];
    var cta = root.querySelector('.newsletter-popup__cta');
    var lastFocused = null;
    var openTimer = null;

    function lockScroll(locked) {
      document.documentElement.style.overflow = locked ? 'hidden' : '';
      document.body.style.overflow = locked ? 'hidden' : '';
    }

    function open() {
      if (root.classList.contains('is-open')) return;

      lastFocused = document.activeElement;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      lockScroll(true);

      /* Focus the field, not the close button: the point of the modal is the
         signup, and the close button is still one Shift+Tab away. */
      /* Opening focus lands on the dismiss control rather than the call to
         action: this is an interstitial the reader did not ask for, so the
         first thing the keyboard reaches should be the way out. */
      var target = closeButton;
      if (target) target.focus({ preventScroll: true });
    }

    function close() {
      if (!root.classList.contains('is-open')) return;

      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      markSeen(root);

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    }

    function trapFocus(event) {
      var items = Array.prototype.filter.call(
        dialog.querySelectorAll(FOCUSABLE),
        function (element) {
          return element.offsetParent !== null;
        }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    Array.prototype.forEach.call(closeButtons, function (button) {
      button.addEventListener('click', function () { close(); });
    });

    /* Dim-only: a click that started inside the card must not close it. */
    root.addEventListener('mousedown', function (event) {
      if (event.target === root) close();
    });

    document.addEventListener('keydown', function (event) {
      if (!root.classList.contains('is-open')) return;
      if (event.key === 'Escape') close();
      if (event.key === 'Tab') trapFocus(event);
    });

    /* Taking the offer counts as "seen" even under session/once, so the page
       the call to action leads to doesn't greet the reader with it again.
       This used to hang off the signup form's submit; the comp replaced that
       form with a plain link, which navigates without ever firing one. */
    if (cta) cta.addEventListener('click', function () { markSeen(root); });

    root.newsletterPopup = { open: open, close: close };

    var designMode = Boolean(window.Shopify && window.Shopify.designMode);
    if (!designMode && (root.dataset.openOnLoad !== 'true' || hasSeen(root))) return;

    /* No delay in the theme editor. The section renders nothing in the page
       flow — the popup is a fixed overlay, invisible until it opens — so a
       merchant who selects it and waits out the open delay is looking at an
       empty outline over the page background and cannot tell whether the
       section is broken. Opening at once makes the selection show its work. */
    var delay = parseInt(root.dataset.delay, 10);
    openTimer = window.setTimeout(open, designMode || isNaN(delay) ? 0 : delay);

    window.addEventListener('pagehide', function () {
      window.clearTimeout(openTimer);
    });
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-newsletter-popup]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var root = event.target.querySelector('[data-newsletter-popup]');
    if (root && root.newsletterPopup) root.newsletterPopup.open();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var root = event.target.querySelector('[data-newsletter-popup]');
    if (root && root.newsletterPopup) root.newsletterPopup.close();
  });
})();
